import React, { Fragment, useEffect, useMemo, useState } from 'react';

import { Divider } from '@rbilabs/universal-components';
import { useRouter } from 'expo-router';
import { usePaymentContext } from 'features/payment';
import { IRevaultInformation } from 'features/payment/types';
import { isApplePay, isGooglePay, shouldReVault } from 'features/payment/utils';
import noop from 'lodash-es/noop';
import { useIntl } from 'react-intl';

import ActionButton, { ActionButtonVariants } from 'components/action-button';
import AccountLayout from 'components/layout/drawer-page';
import LoadingAnimation from 'components/loading-animation';
import PaymentMethodOption from 'components/payment-method-option';
import { CurrentSelectedMethodType } from 'components/payment-method/types';
import UserVerificationModal from 'components/user-verification-modal';
import { usePaymentCheckoutRedesignExperiment } from 'experiments/use-payment-checkout-redesign-experiment';
import { emptyCartContext, useCartContext } from 'state/cart';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { routes } from 'utils/routing';

import { AddGiftCardLink } from './add-gift-card-link';
import { getPaymentMethodOptionProps } from './get-payment-method-options-props';
import { DefaultText, DefaultTextWrapper, Form, Message } from './styled';

interface IPaymentMethodFlatListWithButton {
  setIsAddGiftCardVisible?: (param: boolean) => void;
  setIsTransferGiftCardVisible?: (param: boolean) => void;
}

export const PaymentMethodFlatListWithButton: React.FC<React.PropsWithChildren<
  IPaymentMethodFlatListWithButton
>> = ({ setIsAddGiftCardVisible = noop, setIsTransferGiftCardVisible = noop }) => {
  const {
    deletePaymentMethod,
    loading,
    paymentMethods,
    setDefaultPaymentMethodId,
    defaultPaymentMethodId,
    isOrbital: isOrbitalPaymentEnabled,
    accountToDelete,
    setAccountToDelete,
  } = usePaymentContext();
  const router = useRouter();
  const { formatMessage } = useIntl();
  const isPaymentRedesignEnabled = usePaymentCheckoutRedesignExperiment();
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [isLoading, setLoading] = useState(loading);
  const enableGiftCard = useFlag(LaunchDarklyFlag.ENABLE_GIFT_CARD);
  const hasPaymentMethods = paymentMethods.length > 0;
  const [selectedMethod, setSelectedMethod] = useState(defaultPaymentMethodId);

  const mappedPaymentMethods = useMemo(() => {
    return paymentMethods.map(method => {
      const accountIdentifier = method.accountIdentifier ?? method.fdAccountId ?? '';
      if (
        isPaymentRedesignEnabled &&
        accountIdentifier === CurrentSelectedMethodType.ADD_NEW_CARD
      ) {
        return null;
      }
      const isNativePayment = isApplePay(accountIdentifier) || isGooglePay(accountIdentifier);

      const isCashPayment = !!method.cash;
      const isPayPalPayment = !!method.paypal;
      const isVenmoPayment = !!method.venmo;
      const isCashAppPayment = !!method.cashapp;

      const paymentMethodOptionProps = getPaymentMethodOptionProps({
        defaultPaymentMethodId: selectedMethod,
        setDefaultPaymentMethodId: setSelectedMethod,
        accountIdentifier,
      });

      const revaultInformation: IRevaultInformation = shouldReVault({
        accountIdentifier: method.accountIdentifier || '',
        isOrbitalPaymentEnabled,
        methods: paymentMethods,
      });

      return (
        <Fragment key={accountIdentifier}>
          <PaymentMethodOption
            method={method}
            showDefault
            removable={
              !method.prepaid &&
              !isNativePayment &&
              !isCashPayment &&
              !isPayPalPayment &&
              !isVenmoPayment &&
              !isCashAppPayment
            }
            readonly={isNativePayment}
            deletePaymentMethod={deletePaymentMethod}
            isDefaultPaymentMethod={
              accountIdentifier === (selectedMethod || defaultPaymentMethodId)
            }
            fullWidthDescription={false}
            {...paymentMethodOptionProps}
            revaultInformation={revaultInformation}
            isFlatList
          />
          <Divider />
        </Fragment>
      );
    });
  }, [
    paymentMethods,
    isPaymentRedesignEnabled,
    selectedMethod,
    isOrbitalPaymentEnabled,
    deletePaymentMethod,
    defaultPaymentMethodId,
  ]);

  useEffect(() => {
    setLoading(loading);
  }, [loading]);

  useEffect(() => {
    if (selectedMethod) {
      setDefaultPaymentMethodId(selectedMethod);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMethod]);

  const isInCart = useCartContext() !== emptyCartContext;
  return (
    <AccountLayout
      headerText={formatMessage({ id: 'paymentMethods' })}
      footer={
        <>
          <ActionButton
            mb={'$4'}
            fullWidth
            variant={ActionButtonVariants.OUTLINE}
            onPress={() => {
              if (accountToDelete && isPaymentRedesignEnabled) {
                setAccountToDelete('');
              }
              router.navigate(`${routes.addCard}?isInCart=${isInCart}`);
            }}
            testID="add-new-payment-method"
          >
            {formatMessage({ id: 'addPaymentMethodOrReload' })}
          </ActionButton>
          {enableGiftCard && (
            <AddGiftCardLink
              {...(isPaymentRedesignEnabled
                ? {
                    setIsAddGiftCardVisible,
                    setIsTransferGiftCardVisible,
                  }
                : null)}
            />
          )}
        </>
      }
    >
      {isLoading ? (
        <LoadingAnimation />
      ) : (
        <>
          {!hasPaymentMethods ? (
            <Message>{formatMessage({ id: 'noPaymentMethodOnFile' })}</Message>
          ) : (
            <>
              <DefaultTextWrapper>
                <DefaultText>{formatMessage({ id: 'accountDefault' })}</DefaultText>
              </DefaultTextWrapper>
              <Form testID="account-saved-payments">{mappedPaymentMethods}</Form>
            </>
          )}

          {showVerificationModal && (
            <UserVerificationModal showDialog onDismiss={() => setShowVerificationModal(false)} />
          )}
        </>
      )}
    </AccountLayout>
  );
};
